import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "source/framework/appUtils";
import { SaveDataObj } from "source/framework/saveData";
import { infiniteListPageSize } from "source/models/common/webConsts";
import { IRecordEducationListItem } from "source/models/recordEducation/recordEducationListItem";
import * as recordService from "source/models/services/recordService";
import { ActionContainer } from "source/components/framework/ActionContainer";
import { DialogSettingsCard } from "source/components/framework/Container/DialogSettingsCard";
import { IListUsingComponentState, List } from "source/components/framework/List/List";
import { ListIconButtonsItem } from "source/components/framework/List/ListIconButtons/ListIconButtonsItem/ListIconButtonsItem";
import { ListLinkValue } from "source/components/framework/List/ListLinkValue";
import { ListSearchFilter } from "source/components/framework/List/ListSearchFilter";
import { RecordEducationDialog } from "../RecordEducationDialog";

export interface IRecordEducationListProp {
    companyId: string;
}

interface IRecordEducationListState extends IListUsingComponentState<IRecordEducationListItem> {
    intialized: boolean;
    isLoading: boolean;
    pageSize: number;
    page: number;
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    checkedIds: string[];
}

export class RecordEducationList extends React.Component<IRecordEducationListProp, IRecordEducationListState> {
    constructor(props: IRecordEducationListProp) {
        super(props);
        this.state = {
            intialized: false,
            isLoading: false,
            page: 1,
            hasMore: true,
            filter: "",
            pageSize: infiniteListPageSize,
            sortColumn: "code",
            sortOrderIsAsc: true,
            items: [],
            selectedId: null,
            checkedIds: [],
            detailsItem: null,
            showEditDialog: false,
            editDialogId: "",
            editDialogHasPrevious: false,
            editDialogHasNext: false
        };
    }

    searchItems = async (pageSize: number, page: number, filter: string, sortColumn: string, sortOrderIsAsc: boolean, resetItems: boolean, refreshList: boolean, moveNext: boolean = false, setEducationDialogData: boolean = true) => {
        this.setState({ isLoading: true });
        const result = await AppUtils.callService(() => recordService.getBasicEducationListItems(this.props.companyId, !refreshList ? pageSize : (page * pageSize), !refreshList ? page : 1, filter, sortColumn, sortOrderIsAsc), false, false, () => this.setState({ isLoading: false }));
        if (!result) return;
        const state = this.state;
        const items = List.getListItems(state.items, state.selectedId, state.checkedIds, result.items, resetItems, refreshList);
        let selectedId = items.selectedId;
        let index = items.items.findIndex(i => i.id === selectedId);
        let detailsItem = index > -1 ? items.items[index] : null;
        if (moveNext && detailsItem && index < items.items.length - 1) {
            index = index + 1;
            detailsItem = items.items[index];
            selectedId = items.items[index].id;
        }
        this.setState({
            intialized: true,
            page: !refreshList ? result.page : page,
            hasMore: result.hasMore,
            filter,
            sortColumn,
            sortOrderIsAsc,
            items: items.items,
            selectedId,
            checkedIds: items.checkedIds,
            detailsItem,
            editDialogId: setEducationDialogData ? (moveNext && detailsItem ? detailsItem.id : "") : state.editDialogId,
            editDialogHasPrevious: setEducationDialogData ? index > 0 : state.editDialogHasPrevious,
            editDialogHasNext: setEducationDialogData ? (index < items.items.length - 1 || result.hasMore) : state.editDialogHasNext,
        });
    };

    refreshList = async (setEducationDialogData: boolean = true) => {
        const state = this.state;
        await this.searchItems(state.pageSize, state.page, state.filter, state.sortColumn, state.sortOrderIsAsc, false, true, false, setEducationDialogData);
    };

    componentDidMount(): void {
        const state = this.state;
        this.searchItems(state.pageSize, state.page, state.filter, state.sortColumn, state.sortOrderIsAsc, false, false);
    }

    componentDidUpdate(prevProps: IRecordEducationListProp, prevState: IRecordEducationListState) {
        const props = this.props;
        const state = this.state;
        const prevCompanyId = prevProps.companyId ? prevProps.companyId : "";
        const companyId = props.companyId ? props.companyId : "";
        if (companyId === prevCompanyId) return;
        this.searchItems(state.pageSize, state.page, state.filter, state.sortColumn, state.sortOrderIsAsc, true, false);
    }

    changeFilter = (filter: string) => {
        const state = this.state;
        this.searchItems(state.pageSize, 1, filter, state.sortColumn, state.sortOrderIsAsc, true, false);
    };

    handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        const state = this.state;
        this.searchItems(state.pageSize, !nextPage ? 1 : state.page + 1, state.filter, sortColumn, sortOrderIsAsc, !nextPage, false);
    };

    handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        this.setState({
            selectedId,
            checkedIds,
            detailsItem: this.state.items.find(i => i.id === selectedId)
        });
    };

    handleAdd = () => {
        this.setState(List.getShowEditDialogStateChange(this.state, Base.emptyGuid));
    };

    handleOpenLine = (id: string) => {
        this.setState(List.getShowEditDialogStateChange(this.state, id));
    };

    handleEducationDialogOk = () => {
        this.refreshList(false);
    };

    handleEducationDialogCancel = (refreshList: boolean) => {
        this.setState({
            showEditDialog: false
        });
        if (!refreshList) return;
        this.refreshList(false);
    };

    getRemoveSaveData = (props: IRecordEducationListProp, educationIds: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("companyId", !props.companyId ? null : props.companyId);
        result.append("educationIds", educationIds);
        return result;
    };

    remove = async (educationIds: string[]): Promise<ApiSuccess> => {
        const props = this.props;
        if (educationIds.length < 1) return;
        const saveData = this.getRemoveSaveData(props, educationIds);
        // Call server
        const result = await AppUtils.callService(() => recordService.removeRecordBasicEducations(saveData.obj));
        if (!result) return null;
        this.refreshList();
        return result;
    };

    handleRemove = async () => {
        const state = this.state;
        const selectedIds = List.getSelectedIds(state.selectedId, state.checkedIds);
        const selectedItems = state.items.filter(i => selectedIds.indexOf(i.id) > -1);
        if (selectedItems.length < 1) return;
        if (await AppUtils.showConfirmationDialog(selectedItems.length > 1
            ? Translations.DoYouReallyWantToRemoveSelectedRecordBasicEducations
            : String.format(Translations.DoYouReallyWantToRemoveRecordBasicEducationParameter, selectedItems[0].code), false) === ConfirmationDialogResult.Yes) {
            // Call server
            this.remove(selectedIds);
        }
    };

    setEditDialogItemByIndex = (index: number) => {
        const state = this.state;
        this.setState(List.getEditDialogItemByIndexStateChange(this.state, index, () => this.searchItems(state.pageSize, state.page + 1, state.filter, state.sortColumn, state.sortOrderIsAsc, false, false, true)));
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <>
                <DialogSettingsCard
                    fitToContainerHeight
                    title={props.companyId ? Translations.BasicEducations : Translations.DefaultBasicEducations}
                    info={props.companyId ? Translations.RecordUserModuleInfoBasicEducations : undefined}
                    cardContent={
                        <ActionContainer
                            leftItems={
                                <ListSearchFilter
                                    searchFilter={state.filter}
                                    onSearchClick={this.changeFilter}
                                />
                            }
                            rightItems={
                                <ListIconButtonsItem
                                    addTooltip={Translations.AddBasicEducation}
                                    removeTooltip={Translations.RemoveBasicEducation}
                                    removeDisabled={!state.selectedId && state.checkedIds.length < 1}
                                    onAdd={this.handleAdd}
                                    onRemove={this.handleRemove}
                                />
                            }
                        />
                    }
                    content={
                        <List
                            intialized={state.intialized}
                            isLoading={state.isLoading}
                            hasMore={state.hasMore}
                            globalLoadingInUse={true}
                            height="100%"
                            multiSelect={true}
                            header={{
                                columns: [
                                    { size: 4, column: "code", content: Translations.Name },
                                    { size: 8, column: "comment", content: Translations.Description }
                                ]
                            }}
                            lines={state.items.map((item) => {
                                return {
                                    columns: [
                                        {
                                            size: 4,
                                            column: "code",
                                            content: <ListLinkValue onClick={() => this.handleOpenLine(item.id)}>{item.code}</ListLinkValue>
                                        },
                                        { size: 8, column: "comments", content: item.comment }
                                    ],
                                    id: item.id,
                                };
                            })}
                            sortColumn={state.sortColumn}
                            sortOrderIsAsc={state.sortOrderIsAsc}
                            selectedId={state.selectedId}
                            checkedIds={state.checkedIds}
                            onSetSelectedIds={this.handleSetSelectedIds}
                            onSearchLines={this.handleSearchLines}
                            onOpenLine={this.handleOpenLine}
                            //onLongPress?: (id: string) => void;
                        />
                    } />
                {(state.showEditDialog && !!state.editDialogId) &&
                    <RecordEducationDialog
                        id={state.editDialogId}
                        companyId={props.companyId}
                        hasPrevious={state.editDialogHasPrevious}
                        hasNext={state.editDialogHasNext}
                        onSaved={this.handleEducationDialogOk}
                        onClose={this.handleEducationDialogCancel}
                        onRemove={this.remove}
                        onRefreshList={() => this.refreshList(false)}
                        onPrevious={() => List.moveToPrevious(state, this.setEditDialogItemByIndex)}
                        onNext={() => List.moveToNext(state, this.setEditDialogItemByIndex)}
                    />
                }
            </>
        );
    }
}